import type { ParsedData, Event } from '../../../../interfaces/toolData';
import { parseCSVWithConfig } from '../ErdosMillerParser';
import { HOPTX_CONFIGS } from '../configs/HopTxConfigs';

/**
 * Specialized parser for HopTx SYSTEM files that require special event handling
 * Converts text-based system events into structured Event objects
 */
export class HopTxSystemParser {
  /**
   * Parse HopTx SYSTEM files with proper event handling
   * @param content CSV file content
   * @param filename Optional filename for error reporting
   * @returns ParsedData with events extracted from system messages
   */
  static parse(content: string, filename?: string): ParsedData {
    const baseResult = parseCSVWithConfig(content, HOPTX_CONFIGS.HOPTX_SYSTEM, filename);
    
    // Extract the raw data that ErdosMillerParser already parsed correctly
    // We'll reconstruct it from the curves data since it already handles metadata skipping
    const events = this.extractEventsFromCurves(baseResult.curves || []);
    
    // Keep only Clock Ticks as curves, convert the rest to events
    const curves = baseResult.curves?.filter(curve => 
      curve.name.includes('Clock Ticks') || curve.name.includes('Time')
    ) || [];
    
    return {
      ...baseResult,
      curves,
      events
    };
  }

  /**
   * Extract events from curves that contain text data
   * @param curves Array of curves from the base parser
   * @returns Array of structured Event objects
   */
  private static extractEventsFromCurves(curves: any[]): Event[] {
    const events: Event[] = [];
    
    // Find text-based curves (System Event, Other Info)
    const textCurves = curves.filter(curve => 
      curve.name === 'System Event' || curve.name === 'Other Info'
    );
    
    for (const curve of textCurves) {
      if (curve.name === 'System Event') {
        // Group system events by type
        const eventGroups = new Map<string, Array<{ timestamp: Date, message: string }>>();
        
        for (let i = 0; i < curve.xValues.length; i++) {
          const timestamp = curve.xValues[i];
          const message = curve.yValues[i];
          
          if (message && typeof message === 'string') {
            const eventType = this.categorizeSystemEvent(message);
            
            if (!eventGroups.has(eventType)) {
              eventGroups.set(eventType, []);
            }
            
            eventGroups.get(eventType)!.push({ timestamp, message });
          }
        }
        
        // Convert groups to Event objects
        for (const [eventType, eventList] of eventGroups) {
          events.push({
            name: eventType,
            xValues: eventList.map(e => e.timestamp),
            yValues: eventList.map(e => e.message)
          });
        }
      }
      
      if (curve.name === 'Other Info') {
        // Create configuration events from Other Info
        const timestamps = curve.xValues;
        const messages = curve.yValues.filter((msg: any) => msg && typeof msg === 'string');
        
        if (timestamps.length > 0 && messages.length > 0) {
          events.push({
            name: 'Configuration Events',
            xValues: timestamps.slice(0, messages.length),
            yValues: messages
          });
        }
      }
    }
    
    return events;
  }

  /**
   * Categorize HopTx system events into groups for better organization
   * @param message The system event message
   * @returns Category name for the event
   */
  private static categorizeSystemEvent(message: string): string {
    if (message.includes('Boot') || message.includes('reset')) {
      return 'Boot Events';
    }
    if (message.includes('Log Download')) {
      return 'Log Events';
    }
    if (message.includes('Error') || message.includes('Fault')) {
      return 'Error Events';
    }
    return 'System Events';
  }
}